import Button from '@/app/components/ui/buttons/button'
import Modal from '@/app/components/ui/overlays/modal'
import { StyleVariant } from '@/types/core-types'
import { useCallback, useMemo, useState } from 'react'

type ConfirmationResultCallback = (confirmed: boolean) => void
type ConfirmationModalProps = {
  variant?: StyleVariant
  buttonLabel?: string
}
export function useConfirmationDialog() {
  const [showModal, setShowModal] = useState(false)
  const [prompt, setPrompt] = useState<string>()
  const [variant, setVariant] = useState<StyleVariant>('danger')
  const [callback, setCallback] = useState<ConfirmationResultCallback>(() => () => {})
  const showConfirmationModal = useCallback(
    (message: string, onResult: ConfirmationResultCallback) => {
      setShowModal(true)
      setCallback(() => onResult)
      setPrompt(message)
      variant && setVariant(variant)
    },
    [setShowModal],
  )
  const handleResult = useCallback(
    (success: boolean) => {
      callback && callback(success)
      setShowModal(false)
    },
    [callback],
  )
  const confirmationModalCallback = useCallback(
    ({ variant = 'danger', buttonLabel = 'Ok' }: ConfirmationModalProps) => {
      return (
        <Modal showModal={showModal} setShowModal={setShowModal}>
          <div className="w-full overflow-hidden pb-4 md:max-w-md md:rounded-2xl md:border md:border-gray-100 md:pb-0 md:shadow-xl">
            <div className="flex flex-col items-center justify-center bg-white px-4 py-6 pt-6 text-center md:px-16">
              <h3 className="text-lg font-semibold">Confirm Action</h3>
              <div className="mt-4 text-base">{prompt}</div>
              <div className="mt-6 flex flex-row justify-center space-x-6 text-sm">
                <Button tabIndex={1} className="w-20" variant={variant} onClick={() => handleResult(true)}>
                  {buttonLabel}
                </Button>
                <Button tabIndex={2} variant="neutral" onClick={() => handleResult(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )
    },
    [showModal, setShowModal],
  )

  return useMemo(
    () => ({
      showConfirmationModal,
      ConfirmationDialog: confirmationModalCallback,
    }),
    [showModal, confirmationModalCallback],
  )
}
